/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import {
    ProductList,
    ProductListsDataActions,
    ProductListSearchCriteria,
    ProductListType,
    QueryResultSettings
} from '@msdyn365-commerce/retail-proxy';

/**
 * Input class for the GetProductList data action.
 */
export class GetProductListInput implements IActionInput {
    public readonly productListId: string;

    public readonly queryResultSettings?: QueryResultSettings;

    public constructor(productListId?: string, queryResultSettings?: QueryResultSettings) {
        this.productListId = productListId ? productListId : '';
        this.queryResultSettings = queryResultSettings;
    }

    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string => 'ProductList';

    /**
     * Get Cachetype Name.
     * @returns - Cache name string.
     */
    public getCacheObjectType = (): string => 'ProductList';

    /**
     * Get Cachetype.
     * @returns - CacheType enum.
     */
    public dataCacheType = (): CacheType => 'none';

    /**
     * Set Cache output.
     * @returns - Flag for cache output.
     */
    public shouldCacheOutput = (): boolean => false;
}

/**
 * CreateInput method for the GetProductList method.
 * @param inputData - The input data passed to the createInput method.
 * @param productListId - The product list identifier.
 * @param queryResultSettings - The query result setting required for pagination/ordering.
 * @returns - Product List input.
 */
export const createGetProductListInput = (
    inputData: ICreateActionContext,
    productListId?: string,
    queryResultSettings?: QueryResultSettings
): GetProductListInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to create get product list. User is not authenticated.');
    }

    return new GetProductListInput(productListId ? productListId : '', queryResultSettings);
};

/**
 * The action method for the GetProductList data action.
 * @param input - The action input.
 * @param context - The action context.
 * @returns - ProductList array.
 */
export async function getProductListAction(input: GetProductListInput, context: IActionContext): Promise<ProductList[]> {
    const productListType = ProductListType.QuickOrder.toString();
    const productListSearchCriteria: ProductListSearchCriteria = {
        ProductListType: productListType
    };
    const productLists = await ProductListsDataActions.searchAsync(
        {
            callerContext: context,
            queryResultSettings: input.queryResultSettings
        },
        productListSearchCriteria
    ).then(result => {
        return result;
    });

    return productLists;
}

/**
 * The GetProductList Data Action
 * Returns product list.
 */
export default createObservableDataAction<ProductList[]>({
    id: '@msdyn365-commerce-modules/retail-actions/get-product-list',
    action: getProductListAction as IAction<ProductList[]>,
    input: createGetProductListInput
});
